import { useState, useEffect } from 'react';
import { Search, Trash2, Droplet, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { AdminLayout } from '@/components/AdminLayout';
import { colorApi } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

export default function AdminColors() {
    const [colors, setColors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingColor, setEditingColor] = useState(null);

    const [formData, setFormData] = useState({
        name: ''
    });

    useEffect(() => {
        loadColors();
    }, []);

    const loadColors = async () => {
        try {
            const res = await colorApi.getAll(); // <-- backend endpoint for colors
            setColors(res.data);
        } catch (error) {
            toast.error('Failed to load colors');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (color = null) => {
        if (color) {
            setEditingColor(color);
            setFormData({
                name: color.name,
            });
        } else {
            resetForm();
        }
        setDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({ name: '' });
        setEditingColor(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Color name is required');
            return;
        }

        try {
            if (editingColor) {
                await colorApi.update(editingColor.id, formData);
                toast.success('Color updated!');
            } else {
                await colorApi.create(formData);
                toast.success('Color created!');
            }
            setDialogOpen(false);
            loadColors();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save color');
        }
    };

    const handleDelete = async (colorId) => {
        if (!window.confirm('Are you sure you want to delete this color?')) return;

        try {
            await colorApi.delete(colorId);
            toast.success('Color deleted');
            loadColors();
        } catch (error) {
            toast.error('Failed to delete color');
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const filteredColors = colors.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Colors</h1>
                        <p className="text-stone-400 mt-1">{colors.length} colors</p>
                    </div>
                    <Button
                        onClick={() => handleOpenDialog()}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="add-color-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Color
                    </Button>
                </div>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
                    <Input
                        placeholder="Search by color name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-stone-900 border-stone-700 text-white"
                        data-testid="color-search"
                    />
                </div>

                {/* Colors Table */}
                <div className="bg-stone-900 border border-stone-800 overflow-x-auto">
                    <table className="w-full" data-testid="colors-table">
                        <thead>
                            <tr className="border-b border-stone-800">
                                <th className="text-left p-4 text-stone-400 font-medium">Color</th>
                                <th className="text-left p-4 text-stone-400 font-medium">Created</th>
                                <th className="text-right p-4 text-stone-400 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-8 text-stone-500">Loading...</td>
                                </tr>
                            ) : filteredColors.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-8 text-stone-500">No colors found</td>
                                </tr>
                            ) : (
                                filteredColors.map((color) => (
                                    <tr
                                        key={color.id}
                                        className="border-b border-stone-800 hover:bg-stone-800/50"
                                        data-testid={`color-row-${color.id}`}
                                    >
                                        <td className="p-4 flex items-center gap-3">
                                            <div
                                                className="w-8 h-8 rounded-full border"
                                                style={{ backgroundColor: color.hex }}
                                            />
                                            <span className="font-medium text-white">{color.name}</span>
                                        </td>
                                        <td className="p-4 text-stone-400 text-sm">{formatDate(color.created_at)}</td>
                                        <td className="p-4">
                                            <div className="flex justify-end">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(color.id)}
                                                    className="text-stone-400 hover:text-red-400"
                                                    data-testid={`delete-color-${color.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="bg-stone-900 border-stone-800">
                        <DialogHeader>
                            <DialogTitle className="text-white">
                                {editingColor ? 'Edit Color' : 'Add New Color'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label className="text-stone-300">Color Name *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                                    placeholder="e.g. Red"
                                    data-testid="color-name-input"
                                />
                            </div>

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setDialogOpen(false)}
                                    className="border-stone-700 text-stone-300"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    data-testid="save-color-btn"
                                >
                                    {editingColor ? 'Update Color' : 'Add Color'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
}
